import { IF, NO, UITextView, UIView, YES } from "uicore-ts"
import { RHContractDuration, RHRequiredDocument } from "../../../models/ProcurementModels"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHCPVCodeFormatter } from "../Custom components/RH/Utils_slop/RHCPVCodeFormatter"
import { RHContractDurationFormatter } from "../Custom components/RH/Utils_slop/RHContractDurationFormatter"
import { RHButtons, RHDialogs, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHRichTextView } from "../Custom components/RH/RHRichTextView"

/**
 * A compact, read-only card that summarises one procurement part.
 * Used both in the procurement details view and as a header inside each
 * offer-entry section in RHMakeOffersView.
 */
export class RHProcurementPartView extends UIView {
    private _cpvDisplayRequestID = 0
    
    // "Osa 1" — bold header, hidden for single-part procurements.
    readonly partHeaderLabel = new UITextView()
        .configuredWithObject({
            style: {
                fontWeight: "700",
                fontSize: "16px",
                color: RHColor.dark.stringValue
            }
        })
        .addedAsSubviewToView(this)
    
    // Shown when the part has been closed without a contract — hidden otherwise.
    readonly partClosedWithoutContractStatusLabel = new UITextView()
        .configuredWithObject({
            style: {
                fontSize: "14px",
                fontWeight: "600",
                color: RHColor.danger.stringValue
            },
            hidden: YES
        })
        .addedAsSubviewToView(this)

    // Short description (optional)
    readonly partDescriptionLabel = new RHRichTextView()
        .configuredWithObject({
            style: {
                fontSize: "14px",
                color: RHColor.textMuted.stringValue
            }
        })
        .addedAsSubviewToView(this)
    
    // Estimated cost
    readonly partCostLabel = new UITextView()
        .configuredWithObject({
            style: {
                fontSize: "14px",
                color: RHColor.textMuted.stringValue
            }
        })
        .addedAsSubviewToView(this)
    
    // CPV code
    readonly partCPVLabel = new UITextView()
        .configuredWithObject({
            style: {
                fontSize: "13px",
                color: RHColor.textMuted.stringValue
            }
        })
        .addedAsSubviewToView(this)
    
    // Per-part contract duration (only shown when the part has its own duration)
    readonly partContractDurationLabel = new UITextView()
        .configuredWithObject({
            style: {
                fontSize: "13px",
                color: RHColor.textMuted.stringValue
            }
        })
        .addedAsSubviewToView(this)
    
    // Required documents (optional)
    readonly requiredDocumentsHeaderLabel = new UITextView()
        .configuredWithObject({
            style: {
                fontSize: "13px",
                fontWeight: "600",
                color: RHColor.textPrimary.stringValue
            }
        })
        .addedAsSubviewToView(this)
    
    readonly requiredDocumentsLabel = new RHRichTextView()
        .configuredWithObject({
            style: {
                fontSize: "13px",
                color: RHColor.textSecondary.stringValue
            }
        })
        .addedAsSubviewToView(this)

    readonly cancelPartButton = new RHButton()
        .configuredWithObject({
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding,
            hidden: YES,
            style: {
                fontSize: RHButtons.compactFontSize
            }
        })
        .addedAsSubviewToView(this)
    
    constructor(elementID?: string) {
        super(elementID);
        (this as RHProcurementPartView).configureWithObject({
            style: {
                backgroundColor: RHColor.backgroundLight.stringValue,
                border: `1px solid ${RHColor.borderLight.stringValue}`,
                borderRadius: "6px",
                borderLeft: `4px solid ${RHButtons.primary.backgroundColor.stringValue}`
            }
        })
    }
    
    /**
     * Populate the view from a raw part object.
     * Works with both RHProcurementPart and any plain object.
     *
     * @param showEstimatedCost - When false, the estimated cost row is always
     *   hidden regardless of whether a cost value is present. Defaults to true.
     *   Pass false when the procurement's publishEstimatedCostOfProcurement
     *   field is set to "no".
     * @param globalContractDuration - The procurement-level contract duration,
     *   used as a fallback when the part has no per-part duration of its own.
     * @param isClosedWithoutContract - Shows a warning row when the procurer
     *   has closed this part without awarding a contract. Defaults to NO.
     */
    configureWithPartData(part: {
        partNumber: number
        description?: string
        descriptionFormat?: "plaintext" | "html"
        estimatedCost?: number
        CPV?: string
        requiredDocuments?: RHRequiredDocument[]
        contractDuration?: RHContractDuration
    }, showEstimatedCost: boolean = YES, globalContractDuration?: RHContractDuration, partCount?: number,
        isClosedWithoutContract: boolean = NO, numberOfRootProcurementParts?: number) {
        if (isClosedWithoutContract) {
            this.partClosedWithoutContractStatusLabel.text =
                RHProcurementPartDisplay.closedWithoutContractStatusText(numberOfRootProcurementParts ?? partCount)
            this.partClosedWithoutContractStatusLabel.hidden = NO
        }
        else {
            this.partClosedWithoutContractStatusLabel.text = ""
            this.partClosedWithoutContractStatusLabel.hidden = YES
        }

        this.partHeaderLabel.text = RHProcurementPartDisplay.partHeaderText(part, partCount)
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            this.partHeaderLabel.hidden = NO
        }
        else {
            this.partHeaderLabel.hidden = YES
        }
        
        if (part.description?.trim()) {
            this.partDescriptionLabel.configureWithText(part.description.trim(), part.descriptionFormat)
            this.partDescriptionLabel.hidden = NO
        }
        else {
            this.partDescriptionLabel.configureWithText("")
            this.partDescriptionLabel.hidden = YES
        }
        
        if (showEstimatedCost && typeof part.estimatedCost === "number") {
            this.partCostLabel.text =
                `<strong>Eeldatav maksumus (KM-ta):</strong> ${RHEuroFormatter.formattedAmount(part.estimatedCost)}`
            this.partCostLabel.hidden = NO
        }
        else {
            this.partCostLabel.text = ""
            this.partCostLabel.hidden = YES
        }
        
        if (part.CPV?.trim()) {
            const cpvDisplayRequestID = ++this._cpvDisplayRequestID
            const loadedCPVText = RHCPVCodeFormatter.displayTextForCPVUsingLoadedData(part.CPV)
            this.partCPVLabel.text = `<strong>CPV:</strong> ${RHTextSanitizer.escapedHTML(loadedCPVText)}`
            this.partCPVLabel.hidden = NO
            RHCPVCodeFormatter.displayTextForCPV(part.CPV).then(cpvText => {
                if (cpvDisplayRequestID !== this._cpvDisplayRequestID) {
                    return
                }
                this.partCPVLabel.text = `<strong>CPV:</strong> ${RHTextSanitizer.escapedHTML(cpvText)}`
                this.setNeedsLayoutUpToRootView()
            }).catch(() => {
                // Keep the stored CPV value visible if the CPV register cannot be loaded.
            })
        }
        else {
            this._cpvDisplayRequestID++
            this.partCPVLabel.text = ""
            this.partCPVLabel.hidden = YES
        }
        
        // Legacy procurements can still carry one common duration outside the parts.
        const effectiveDuration = part.contractDuration ?? globalContractDuration
        const durationText = RHContractDurationFormatter.formattedText(effectiveDuration)
        if (durationText) {
            let prefix = "Lepingu kestus kõikidele osadele"
            if (part.contractDuration) {
                prefix = "Lepingu kestus"
            }
            this.partContractDurationLabel.text =
                `<strong>${prefix}:</strong> ${RHTextSanitizer.escapedHTML(durationText)}`
            this.partContractDurationLabel.hidden = NO
        }
        else {
            this.partContractDurationLabel.text = ""
            this.partContractDurationLabel.hidden = YES
        }
        
        const requiredDocuments = part.requiredDocuments ?? []
        if (requiredDocuments.length > 0) {
            this.requiredDocumentsHeaderLabel.text = "Nõutud dokumendid:"
            this.requiredDocumentsHeaderLabel.hidden = NO
            const requiredDocumentsHTML = requiredDocuments
                .map((doc, index) => {
                    const documentName = RHTextSanitizer.escapedHTML(doc.name)
                    if (doc.description) {
                        if (doc.descriptionFormat === "html") {
                            return `<div>${index + 1}. ${documentName}</div>${RHTextSanitizer.sanitizedHTML(doc.description)}`
                        }
                        return `${index + 1}. ${documentName} — ${RHTextSanitizer.escapedHTML(doc.description).replace(/\r?\n/g, "<br>")}`
                    }
                    return `${index + 1}. ${documentName}`
                })
                .join("<br>")
            this.requiredDocumentsLabel.configureWithText(requiredDocumentsHTML, "html")
            this.requiredDocumentsLabel.hidden = NO
        }
        else {
            this.requiredDocumentsHeaderLabel.text = ""
            this.requiredDocumentsHeaderLabel.hidden = YES
            this.requiredDocumentsLabel.configureWithText("")
            this.requiredDocumentsLabel.hidden = YES
        }
    }
    
    
    get _visibleRows(): UITextView[] {
        return [
            this.partHeaderLabel,
            this.partClosedWithoutContractStatusLabel,
            this.partCostLabel,
            this.partCPVLabel,
            this.requiredDocumentsHeaderLabel,
            this.requiredDocumentsLabel,
            this.partContractDurationLabel,
            this.partDescriptionLabel
        ].filter(view => view.hidden === NO)
    }


    _gapBeforeRow(row: UITextView): number {
        return IF(row === this.partDescriptionLabel)(
            () => RHSpacing.medium
        ).ELSE(
            () => this.core.paddingLength * 0.25
        )
    }


    _rowGaps(rows: UITextView[]): number[] {
        return rows.slice(1).map(row => this._gapBeforeRow(row))
    }


    override intrinsicContentHeight(constrainingWidth = 0): number {
        const contentInset = this.core.paddingLength * 0.75
        const contentWidth = [0, constrainingWidth - contentInset * 2].max()
        const rows = this._visibleRows
        const rowGaps = this._rowGaps(rows)
        const rowsHeight = rows.reduce((height, row) =>
            height + row.intrinsicContentHeight(contentWidth),
        0)
        const actionHeight = IF(this.cancelPartButton.hidden === NO)(
            () => RHDialogs.share.buttonHeight + RHDialogs.share.buttonToContentGap
        ).ELSE(
            () => 0
        )
        return contentInset * 2 + actionHeight + rowsHeight + rowGaps.reduce(
            (height, rowGap) => height + rowGap,
            0
        )
    }


    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(
            padding * 0.75
        )
        
        const visibleRows = this._visibleRows
        const cancelButtonWidth = [
            RHDialogs.share.buttonWidth,
            this.cancelPartButton.titleLabel.intrinsicContentWidth(RHDialogs.share.buttonHeight) +
                RHButtons.compactContentPadding * 2
        ].max()

        bounds
            .IF(this.cancelPartButton.hidden === NO)
            .rectangleWithHeight(RHDialogs.share.buttonHeight)
            .rectangleWithWidth(cancelButtonWidth, 1)
            .assignedAsFrameOfView(this.cancelPartButton)
            .rectangleForNextRow(RHDialogs.share.buttonToContentGap, 0)
            .rectangleWithX(bounds.x)
            .rectangleWithWidth(bounds.width)
            .ELSE()
            .rectangleWithHeight(0)
            .ENDIF()
            .framesByDistributingViewsAsColumn(
                visibleRows,
                this._rowGaps(visibleRows)
            )
    }
    
}
